import { AlertTriangle, Ban, Building2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  ContainerLineEvent,
  ContainerLineStepPlan,
  ContainerOrderLine,
  ContainerRouteStop,
} from "../../types/containerOrders";
import {
  completedStepCount,
  lineSteps,
  routeStopsOf,
} from "../../utils/containerLineSteps";
import {
  splitDuration,
  stopScheduleMetric,
} from "../../utils/containerLineSchedule";
import type {
  ScheduleTone,
  StopScheduleMetric,
} from "../../utils/containerLineSchedule";

interface ContainerLineTimelineProps {
  line: ContainerOrderLine;
  className?: string;
  /**
   * Drop the heading. For callers that already name the rail themselves — the
   * container page's collapsible sidebar carries the title next to its close
   * control — where printing it again would be the same words twice.
   */
  hideTitle?: boolean;
}

const stamp = (value?: string | Date | null) =>
  value
    ? new Date(value).toLocaleString([], {
        dateStyle: "short",
        timeStyle: "short",
      })
    : null;

/**
 * The planned side of a metric, printed at the precision it was booked at.
 *
 * A date-only stop is stored as local midnight, and printing that as "8/11/26,
 * 12:00 AM" would put a time on a booking nobody made — and invite the reader
 * to measure the actual stamp against it.
 */
const plannedStamp = (metric: StopScheduleMetric) =>
  metric.precision === "day"
    ? metric.plannedAt.toLocaleDateString([], { dateStyle: "short" })
    : stamp(metric.plannedAt);

/** The schedule badge, keyed by how the stop went. */
const badgeClass = (tone: ScheduleTone) =>
  tone === "late"
    ? "bg-red-50 text-red-600 border border-red-200"
    : tone === "atRisk"
      ? "bg-amber-50 text-amber-700 border border-amber-200"
      : "bg-green-50 text-green-600 border border-green-200";

/**
 * One row of the timeline: the container existing, the office sending it out,
 * then a step of the run.
 *
 * The two opening rows are `start` rows rather than steps because nothing was
 * driven for either — they carry a moment and nothing else: no stop, no
 * schedule to be judged against, no note from a driver. What separates them
 * from each other is that the first has always happened by the time anybody is
 * reading the timeline, and the second may still be owed.
 */
type TimelineRow =
  | {
      kind: "start";
      key: string;
      title: string;
      actual: string | null;
      /** The release, before it has been taken — the one openable start row. */
      pending?: string;
    }
  | {
      kind: "step";
      key: string;
      title: string;
      subtitle: string | null;
      state: "done" | "current" | "later";
      actual: string | null;
      note: string | null;
      fromOffice: boolean;
      metric: StopScheduleMetric | null;
    };

/**
 * One container's journey, as a timeline.
 *
 * Bound to the container and to nothing above it. The order's status is a
 * rollup — `syncContainerOrderStatus` derives it from however many containers
 * the order holds — so it reads "in progress" while thirty boxes sit at five
 * different stages, and a timeline drawn from it would describe none of them.
 * This one is drawn from this container's own route and step log.
 *
 * The rows are the container's *own* steps, not a fixed six. Two containers on
 * the same order can have different runs — one stops at a weighbridge, one is
 * not collecting from a terminal at all — and a timeline that showed six rows
 * regardless would describe a journey neither of them is making. Each step row
 * names the stop it belongs to, which is what makes a route with three added
 * stops readable at all: "arrived" three times in a column is not a timeline.
 *
 * What a step row can say, in the order the reader wants it:
 *   · booked   — planned against actual, wherever the stop carries a date, with
 *                the badge that judges one against the other
 *   · recorded — the actual timestamp alone, for a stop with nothing booked
 *   · later    — pending, because nothing about it is known yet
 *
 * A stop that is not being made gets one row of its own rather than two blank
 * steps, and carries the reason. That is the whole difference between "we are
 * not doing this" and "we have not done this yet", and it is the question the
 * timeline exists to answer.
 *
 * `cancelled` is not a row. It is an override lying across the whole run, so it
 * is said once, above, and the progress underneath is left standing: a
 * container called off at the loading site had still reached the loading site,
 * and un-cancelling puts exactly that back.
 */
export function ContainerLineTimeline({
  line,
  className = "",
  hideTitle = false,
}: ContainerLineTimelineProps) {
  const { t } = useTranslation("containerOrders");

  const cancelled = line.status === "cancelled";
  /**
   * Whether dispatch has sent this container out.
   *
   * Read off the stored status rather than `isLinePlanned`, because the two
   * questions differ on exactly one container: a cancelled one, whose stored
   * word is `cancelled` either way. Nothing below needs to tell those apart —
   * a cancelled container is not waiting on a step whether it was released or
   * not — and the release row above draws its own date from `plannedAt`.
   */
  const released = line.status !== "created";

  /**
   * "2h 15m", "3h", "20m" — or "2d", for a stop booked to a day only. The unit
   * comes off the metric rather than being chosen here: a day booking is
   * measured in whole days, and rendering those as "48h" would claim a
   * precision the slot never had.
   */
  const deltaLabel = (delta: NonNullable<StopScheduleMetric["delta"]>) => {
    if (delta.unit === "days")
      return t("linePage.timeline.duration.d", { d: Math.abs(delta.value) });
    const { h, m } = splitDuration(delta.value);
    if (h && m) return t("linePage.timeline.duration.hm", { h, m });
    if (h) return t("linePage.timeline.duration.h", { h });
    return t("linePage.timeline.duration.m", { m });
  };

  /**
   * The badge, or nothing.
   *
   * Nothing for a step that is not due yet: an arrival booked for tomorrow is
   * neither on time nor late, and a green "on time" against it would be a
   * claim about something that has not happened.
   */
  const badgeLabel = (metric: StopScheduleMetric) => {
    if (metric.tone === "onTime") return t("linePage.timeline.badges.onTime");
    if (!metric.delta) return null;
    const duration = deltaLabel(metric.delta);
    if (metric.tone === "late")
      return t("linePage.timeline.badges.late", { duration });
    if (metric.tone === "early")
      return t("linePage.timeline.badges.early", { duration });
    if (metric.tone === "atRisk")
      return t("linePage.timeline.badges.atRisk", { duration });
    return null;
  };

  // The completed prefix, not the total number of stamps: the run is strictly
  // sequential, so a gap means the container is only as far as the gap. Shared
  // with the status field on the container's form, which offers the next step
  // this same count decides.
  const stops = routeStopsOf(line);
  const steps = lineSteps(line);
  const done = completedStepCount(line);

  // For each step, whether anything *after* it carries a stamp. A container
  // whose log jumps over a step — an import, a stamp cleared by hand, a driver
  // app that recorded the loading before the collection — is not still waiting
  // on that step, so nothing about it should be forecast as running late.
  // Walked backwards once, rather than re-scanning the tail per row.
  const stampedAfter: boolean[] = new Array(steps.length).fill(false);
  for (let i = steps.length - 2; i >= 0; i -= 1) {
    const { stopIndex, field } = steps[i + 1];
    stampedAfter[i] = stampedAfter[i + 1] || Boolean(stops[stopIndex]?.[field]);
  }

  /** What a stop is called on its rows. */
  const titleOf = (stop: ContainerRouteStop | undefined) => {
    if (!stop) return "";
    if (stop.role !== "custom") return t(`route.${stop.role}`);
    return (
      stop.label?.trim() ||
      stop.companyName?.trim() ||
      t(`route.stopKinds.${stop.kind ?? "other"}`)
    );
  };

  /**
   * The event that recorded this step.
   *
   * Matched on the stop as well as the step name, because the name alone
   * stopped being unique the moment a route could hold two added stops — both
   * call their halves `arrived_stop`. Events stored before that carry no
   * `stopId` and are matched on the name, which could only mean one thing then.
   */
  const eventFor = (step: ContainerLineStepPlan) =>
    line.events?.find(
      (e: ContainerLineEvent) =>
        e.type === step.step && (!e.stopId || String(e.stopId) === step.stopId),
    );

  /**
   * The rows, walked stop by stop rather than step by step.
   *
   * Walking the stops is what lets a stop that is not being made contribute one
   * row saying so instead of two blank steps — and it keeps each stop's two
   * rows together under its own name, which is the only way a route with three
   * added stops reads as a journey rather than as six identical "arrived" lines.
   */
  /**
   * The container coming into existence, and the office deciding to send it.
   *
   * These used to be one row, labelled "Planned" and dated by `createdAt`,
   * because a container was created and dispatched in the same act — there was
   * nothing in between to draw. There is now: a container is generated with its
   * order and released later, one box at a time, and the gap between the two is
   * exactly what a dispatcher looking at a batch of thirty wants to see. So they
   * are two rows, each with its own date.
   *
   * The release row is the only row above the steps that can be outstanding,
   * and it says so in the driver's terms rather than the timeline's usual
   * "Pending" — what is being waited on is not an event out on the road, it is
   * a decision in this office.
   */
  const rows: TimelineRow[] = [
    {
      kind: "start",
      key: "created",
      title: t("linesBlock.statuses.created"),
      actual: stamp(line.createdAt),
    },
    {
      kind: "start",
      key: "planned",
      title: t("linesBlock.statuses.planned"),
      // Containers written before the release was recorded separately carry no
      // stamp and were planned on creation — see `isLinePlanned`. Their own
      // creation date is the honest thing to draw against the release, and it is
      // what the migration writes into the field.
      actual: line.plannedAt
        ? stamp(line.plannedAt)
        : line.status === "created"
          ? null
          : stamp(line.createdAt),
      pending: t("linePage.timeline.notPlanned"),
    },
  ];

  // Step rows are numbered against the run, so a skipped stop between them does
  // not consume a number the driver never sees.
  let stepIndex = 0;

  stops.forEach((stop) => {
    // A stop that is not being made is not on the journey, so it is not on the
    // timeline. It contributes no steps either way; showing it as a struck-out
    // row made the reader work out which entries were about to happen and which
    // never would, which is the one question a timeline exists to answer without
    // being asked. Why it is not being made is on the stop's own card, on the
    // driver's screen, and in the event log — none of that is lost by keeping
    // this column to the run itself.
    if (stop.enabled === false) return;

    steps
      .filter((step) => step.stopId === String(stop._id))
      .forEach((step) => {
        const index = stepIndex;
        stepIndex += 1;

        const recorded = index < done;
        const event = eventFor(step);

        // Expected vs actual, for the stops that were booked.
        //
        // Measured on the *completion* stamp — collected, loaded, cleared —
        // because that is what the stop's slot books: the work at the stop done.
        // Arriving at the gate inside the slot and leaving hours later is not a
        // slot kept, so the arrival rows carry no judgement, only what was
        // recorded.
        //
        // Null for a stop with no usable date at all, where the row falls back
        // to the plain "recorded / pending" rendering because there is no
        // commitment to have missed.
        const metric =
          step.field === "completedAt"
            ? stopScheduleMetric(stop, recorded ? stop[step.field] : null, {
                // A cancelled container is not running late for an appointment
                // it is no longer keeping — its unreached stops stay unjudged.
                // Nor is a step the container demonstrably drove past without a
                // stamp: a later stamp makes the gap missing data, not a delay,
                // and "+900h late" against it is a number about nothing.
                forecast: !cancelled && !(!recorded && stampedAfter[index]),
              })
            : null;

        rows.push({
          kind: "step",
          key: `${stop._id}-${step.field}`,
          title: t(`linesBlock.steps.${step.step}`),
          // The stop's own name under the step, which is what makes two
          // `arrived_stop` rows on one route tell each other apart.
          subtitle: step.role === "custom" ? titleOf(stop) : null,
          state: recorded
            ? "done"
            : // Only the very next step is current, and only on a container
              // that is actually waiting for it. A cancelled one is not waiting
              // for anything; an unreleased one is waiting on the office, and
              // the release row above is where that is said — marking the first
              // step current as well would put two "you are here" markers on one
              // timeline and point the second at a driver who cannot see the
              // container.
              index === done && !cancelled && released
              ? "current"
              : "later",
          actual: recorded ? stamp(stop[step.field]) : null,
          note: recorded ? (event?.note ?? null) : null,
          // Said on the row rather than left to be inferred from an empty
          // location. A step the office recorded is a real step at a real time,
          // but nobody was standing there — and a reader who assumes otherwise
          // is reading the stamp as evidence of a presence it never claimed.
          fromOffice: recorded && event?.source === "system",
          metric,
        });
      });
  });

  return (
    <div className={className}>
      {!hideTitle && (
        <p className="mb-4 px-1 text-xs font-semibold uppercase tracking-wider text-mist">
          {t("linePage.timeline.title")}
        </p>
      )}

      {cancelled && (
        <p className="mb-3 flex items-start gap-1.5 rounded-lg bg-bone px-2 py-1.5 text-[11px] leading-snug text-mist">
          <Ban className="mt-0.5 h-3 w-3 shrink-0" />
          {t("linePage.timeline.cancelled")}
        </p>
      )}

      <div className={`relative ${cancelled ? "opacity-60" : ""}`}>
        {rows.map((row, index) => {
          const isLast = index === rows.length - 1;
          const state =
            row.kind === "step"
              ? row.state
              : // A start row is done once it has a date. The release is the one
                // that can lack one, and until it does it is what the container
                // is waiting on — which is the definition of `current`.
                row.actual
                ? "done"
                : "current";
          // Coloured as far as the container has actually got, and no further.
          const nextRow = rows[index + 1];
          const connector =
            nextRow &&
            (nextRow.kind === "step"
              ? nextRow.state === "done"
              : // A start row reached is a start row with a date on it.
                Boolean(nextRow.actual))
              ? "bg-green-400"
              : "bg-secondary-200";

          return (
            <div key={row.key} className="relative flex gap-3 pb-5 last:pb-0">
              {/* Connector, coloured only as far as the container has got — the
                  line itself is the progress bar. */}
              {!isLast && (
                <div
                  className={`absolute left-[11px] top-6 h-[calc(100%-10px)] w-0.5 ${connector}`}
                />
              )}

              <div
                className={`relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  state === "done"
                    ? "bg-green-500 text-white"
                    : state === "current"
                      ? "bg-primary text-white"
                      : "bg-secondary-100 text-mist"
                }`}
              >
                {state === "done" ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : (
                  index + 1
                )}
              </div>

              <div className="min-w-0 flex-1 pt-0.5">
                {/* Label and badge share a wrapping row: the label never breaks
                    mid-word, and the badge sits right after it, dropping to its
                    own line when the sidebar is too narrow. */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p
                    className={`text-sm font-medium leading-tight ${
                      state === "later" ? "text-mist" : "text-ink"
                    }`}
                  >
                    {row.title}
                  </p>
                  {row.kind === "step" && row.metric && badgeLabel(row.metric) && (
                    <span
                      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none ${badgeClass(
                        row.metric.tone,
                      )}`}
                      // Says which of the two rulers is in use — a reader
                      // seeing "+2d" against a stop booked only to a day
                      // should not have to work out why it is not "+38h 4m".
                      title={t(
                        `linePage.timeline.${
                          row.metric.tone === "atRisk"
                            ? "atRiskHint"
                            : "metricHint"
                        }.${row.metric.precision}`,
                      )}
                    >
                      {row.metric.tone === "atRisk" && (
                        <AlertTriangle className="mr-1 h-3 w-3" />
                      )}
                      {badgeLabel(row.metric)}
                    </span>
                  )}
                </div>

                {/* Which stop this step was at. Only on the added ones: the
                    three anchors are named by the step itself ("arrived at the
                    loading site"), and repeating that underneath would be the
                    same sentence twice. */}
                {row.kind === "step" && row.subtitle && (
                  <p className="mt-0.5 truncate text-[11px] leading-tight text-secondary-500">
                    {row.subtitle}
                  </p>
                )}

                {row.kind === "start" ? (
                  row.actual ? (
                    <p className="mt-0.5 text-xs text-green-600">{row.actual}</p>
                  ) : (
                    <p className="mt-0.5 text-xs text-secondary-300">
                      {row.pending ?? t("linePage.timeline.pending")}
                    </p>
                  )
                ) : row.metric ? (
                  // Both moments, one under the other, so the badge can be
                  // checked rather than taken on trust.
                  <div className="mt-0.5 space-y-0.5 text-[11px] leading-tight text-mist">
                    <p>
                      {t("linePage.timeline.planned")}{" "}
                      {/* Copper only while this is the step being waited on and
                          nothing has been recorded against it — the emphasis a
                          date the reader still has to act on wants. */}
                      <span
                        className={`font-medium ${
                          !row.actual && row.state === "current"
                            ? "text-copper"
                            : "text-secondary-600"
                        }`}
                      >
                        {plannedStamp(row.metric)}
                      </span>
                    </p>
                    {row.actual && (
                      <p>
                        {t("linePage.timeline.actual")}{" "}
                        <span
                          className={`font-medium ${
                            row.metric.tone === "late"
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {row.actual}
                        </span>
                      </p>
                    )}
                  </div>
                ) : row.actual ? (
                  <p className="mt-0.5 text-xs text-green-600">{row.actual}</p>
                ) : (
                  <p className="mt-0.5 text-xs text-secondary-300">
                    {t("linePage.timeline.pending")}
                  </p>
                )}

                {row.kind === "step" && row.fromOffice && (
                  <p className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-mist">
                    <Building2 className="mt-px h-3 w-3 shrink-0" />
                    {t("linePage.timeline.fromOffice")}
                  </p>
                )}

                {/* Whatever the driver typed when recording the step. It is the
                    only part of the log written by a person, and it is usually
                    the reason a stop went the way it did. */}
                {row.kind === "step" && row.note && (
                  <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-snug text-secondary-500">
                    {row.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
